import apiClient from './config';
import { AuthResponse, LoginCredentials, RegisterData, User } from '../../types/auth';

export interface CompanyChoice {
  companyId: number;
  companyName: string;
  role: string;
  userId: number;
}

export interface LoginResponse {
  requiresCompanySelection?: boolean;
  companyChoices?: CompanyChoice[];
  email?: string;
  token?: string;
  mustChangePassword?: boolean;
  user?: any;
  tokens?: { accessToken: string; refreshToken: string; expiresIn: string };
}

export const authAPI = {
  login: async (credentials: LoginCredentials & { companyId?: number }): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data.data;
  },

  // Register user
  register: async (data: RegisterData): Promise<AuthResponse['data']> => {
    const response = await apiClient.post('/auth/register', data);
    return response.data.data;
  },

  // Get user profile
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    // Backend returns user data directly in response.data.data (not nested under .user)
    return response.data.data;
  },

  // Update user profile
  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await apiClient.put('/auth/me', data);
    return response.data.data;
  },

  // Change password
  changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
    await apiClient.post('/auth/change-password', data);
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<{ accessToken: string }> => {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/request-password-reset', { email });
  },

  // Reset password
  resetPassword: async (data: { token: string; password: string }): Promise<void> => {
    await apiClient.post('/auth/reset-password', data);
  },

  // Verify email
  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post('/auth/verify-email', { token });
  },

  // Resend verification email
  resendVerification: async (email: string): Promise<void> => {
    await apiClient.post('/auth/resend-verification', { email });
  },

  // Logout (optional - mainly for server-side cleanup)
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors on logout
      console.warn('Logout API call failed:', error);
    }
  },
};
